import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { DepreciationCalculator } from '@/lib/depreciation-calculator';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Play,
  FileText,
  CheckCircle2,
  Clock,
  Sparkles,
  Eye,
  Save,
  X,
  RotateCcw,
  Plus,
  Trash2,
  Settings2,
  TrendingDown,
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { AssetSelectionDialog } from '@/components/depreciation/asset-selection-dialog';
import { BatchDetailGrid } from '@/components/depreciation/batch-detail-grid';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { ComingSoonModal } from '@/components/coming-soon-modal';
import { DemoModeWarningModal } from '@/components/ui/demo-mode-warning-modal';
import { FeatureSettingsGuard } from '@/components/features/FeatureSettingsGuard';
import { ProductTour } from '@/components/ui/product-tour';
import { EvoFeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { useTour } from '@/contexts/TourContext';
import { useDepreciationTour } from '@/hooks/use-depreciation-tour';
import type { Asset } from '@/types/asset';
import type { DepreciationBatch, DepreciationItem, CurrentPeriodSummary, EligibleAssetForDepreciation } from '@/types/depreciation';
import { mockCurrentPeriod, mockCompletedBatches } from '@/lib/mock-depreciation-data';
import { mockAssets } from '@/lib/mock-data';
import { dataService } from '@/services/data-service';
import { useMode } from '@/providers/ModeProvider';
import { exportToExcel, generateFilename, formatCurrencyForExport, type ExportColumn } from '@/lib/export-utils';
import { formatCurrency } from '@/lib/format-currency';
import { formatApiErrorMessage } from '@/lib/format-utils';

type SortDirection = 'asc' | 'desc' | null;

interface SortIconProps {
  readonly columnKey: string;
  readonly sortKey: string | null;
  readonly sortDirection: SortDirection;
}

function SortIcon({ columnKey, sortKey, sortDirection }: SortIconProps): React.ReactElement | null {
  if (sortKey !== columnKey) {
    return null;
  }
  return sortDirection === 'asc' ? (
    <ArrowUp className="ml-2 h-4 w-4" />
  ) : (
    <ArrowDown className="ml-2 h-4 w-4" />
  );
}

// Helper function to get sortable value from a batch based on the sort key
function getBatchSortValue(batch: DepreciationBatch, key: string): string | number {
  switch (key) {
    case 'batchReference':
      return batch.batchReference;
    case 'dateOfRun':
      return new Date(batch.dateOfRun).getTime();
    case 'assetCount':
      return batch.items?.length ?? 0;
    case 'totalDepreciation':
      return batch.totalDepreciation ?? 0;
    case 'postedBy':
      return batch.postedBy ?? '';
    case 'postedDate':
      return batch.postedDate ? new Date(batch.postedDate).getTime() : 0;
    case 'status':
      return batch.status;
    default:
      return 0;
  }
}

// Helper function to compare two values for sorting
function compareSortValues(aValue: string | number | null, bValue: string | number | null, direction: SortDirection): number {
  if (aValue == null && bValue == null) return 0;
  if (aValue == null) return 1;
  if (bValue == null) return -1;
  if (aValue < bValue) return direction === 'asc' ? -1 : 1;
  if (aValue > bValue) return direction === 'asc' ? 1 : -1;
  return 0;
}

// Helper function to generate page numbers for pagination - extracted to reduce nesting depth (S2004)
function generatePageNumbers(currentPage: number, totalPages: number): (number | string)[] {
  const pageNumbers: (number | string)[] = [];
  const maxVisiblePages = 7;
  
  if (totalPages <= maxVisiblePages) {
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(i);
    }
  } else {
    pageNumbers.push(1);
    
    if (currentPage > 3) {
      pageNumbers.push('...');
    }
    
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    
    for (let i = start; i <= end; i++) {
      pageNumbers.push(i);
    }
    
    if (currentPage < totalPages - 2) {
      pageNumbers.push('...');
    }
    
    pageNumbers.push(totalPages);
  }
  
  return pageNumbers;
}

function DepreciationPageContent():React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDemo, setMode } = useMode();
  
  // Product Tour
  const { isTourOpen, tourSteps, startTour, closeTour, completeTour } = useDepreciationTour();
  const { setOnStartTour } = useTour();
  
  // Register tour with context
  useEffect(() => {
    setOnStartTour(() => startTour);
    return () => setOnStartTour(null);
  }, [setOnStartTour, startTour]);
  
  const [showAssetSelection, setShowAssetSelection] = useState(false);
  const [currentBatch, setCurrentBatch] = useState<DepreciationBatch | null>(null);
  const [period, setPeriod] = useState(mockCurrentPeriod);
  const [currentPeriodSummary, setCurrentPeriodSummary] = useState<CurrentPeriodSummary | null>(null);
  const [isLoadingPeriodSummary, setIsLoadingPeriodSummary] = useState(false);
  const [isLoadingEligibleAssets, setIsLoadingEligibleAssets] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showProcessConfirm, setShowProcessConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showModifiedHighlight, setShowModifiedHighlight] = useState(false);
  const [completedBatches, setCompletedBatches] = useState<DepreciationBatch[]>(mockCompletedBatches);
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [showDemoWarning, setShowDemoWarning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc' | null;
  }>({
    key: 'dateOfRun',
    direction: 'desc',
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const pageSize = 25;

  // Register tour with context
  useEffect(() => {
    setOnStartTour(() => startTour);
    return () => setOnStartTour(null);
  }, [setOnStartTour, startTour]);

  useEffect(() => {
    const fetchBatches = async () => {
      setIsLoadingBatches(true);
      try {
        const batches = await dataService.getDepreciationBatches();
        if (batches && batches.length > 0) {
          const sortedBatches = batches.sort((a: any, b: any) => 
            new Date(b.dateOfRun).getTime() - new Date(a.dateOfRun).getTime()
          );
          setCompletedBatches(sortedBatches);
        } else {
          setCompletedBatches(mockCompletedBatches);
        }
      } catch (error) {
        console.error('Error fetching depreciation batches:', error);
        setCompletedBatches(mockCompletedBatches);
      } finally {
        setIsLoadingBatches(false);
      }
    };

    if (!currentBatch) {
      fetchBatches();
    }
  }, [currentBatch, refreshTrigger]);

  // Fetch current period summary from API
  useEffect(() => {
    const fetchCurrentPeriodSummary = async () => {
      setIsLoadingPeriodSummary(true);
      try {
        const summary = await dataService.getCurrentPeriodDepreciationSummary();
        setCurrentPeriodSummary(summary);
        // Update period display with API data
        setPeriod({
          ...mockCurrentPeriod,
          period: summary.period,
          status: summary.isUpToDate ? 'Completed' : 'Not Started',
          assetsInScope: summary.assetsInScope,
          priorPeriodDepreciation: summary.priorPeriodDepreciation,
          exceptionsLastRun: summary.exceptionsLastRun,
          expectedCompletionBy: summary.expectedCompletionBy,
        });
      } catch (error) {
        console.error('Error fetching current period summary:', error);
        // Fall back to mock data on error
        setCurrentPeriodSummary(null);
      } finally {
        setIsLoadingPeriodSummary(false);
      }
    };

    if (!currentBatch) {
      fetchCurrentPeriodSummary();
    }
  }, [currentBatch, refreshTrigger]);

  // Reset to page 1 when search or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortConfig]);

  // Handle selected assets passed from assets page and URL parameters
  useEffect(() => {
    const state = location.state as { selectedAssets?: Asset[]; refresh?: boolean; viewBatch?: boolean } | null;
    const searchParams = new URLSearchParams(location.search);
    const batchId = searchParams.get('batch');
    
    // Handle refresh from breadcrumb click (when clicking current page without batch param)
    if (state?.refresh && !batchId) {
      console.log('Soft reloading current page');
      // Clear the current batch and reset page state
      setCurrentBatch(null);
      setSearchQuery('');
      setCurrentPage(1);
      setSortConfig({ key: 'dateOfRun', direction: 'desc' });
      setRefreshTrigger(prev => prev + 1); // Trigger data refetch
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
      return;
    }
    
    // Handle clearing batch when navigating back to main page (no batch param)
    if (!batchId && currentBatch) {
      setCurrentBatch(null);
      setRefreshTrigger(prev => prev + 1);
      return;
    }
    
    if (state?.selectedAssets && state.selectedAssets.length > 0) {
      handleAssetsSelected(state.selectedAssets);
      // Clear the state to prevent re-triggering on navigation
      window.history.replaceState({}, document.title);
    }
  }, [location.state, location.search]);

  // Sort handler - cycles through: unsorted → asc → desc → unsorted
  const handleSort = (key: string) => {
    setSortConfig((prevConfig) => {
      // If clicking a different column, start with asc
      if (prevConfig.key !== key) {
        return { key, direction: 'asc' };
      }
      // If same column, cycle through: asc → desc → unsorted
      if (prevConfig.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      if (prevConfig.direction === 'desc') {
        return { key: null, direction: null };
      }
      // If unsorted, start with asc
      return { key, direction: 'asc' };
    });
  };

  const handleStartRun = () => {
    if (isDemo) {
      setShowDemoWarning(true);
      return;
    }
    setShowAssetSelection(true);
  };

  const handleStartRunWithAllAssets = async () => {
    if (isDemo) {
      setShowDemoWarning(true);
      return;
    }
    
    // Fetch eligible assets from API and start the run with pre-calculated values
    setIsLoadingEligibleAssets(true);
    try {
      const apiEligibleAssets = await dataService.getCurrentPeriodEligibleAssets();
      
      if (apiEligibleAssets.length === 0) {
        toast.info('No eligible assets', {
          description: 'No assets are eligible for depreciation in the current period.',
        });
        return;
      }
      
      // Convert API eligible assets to Asset format for handleAssetsSelected
      // The API returns pre-calculated depreciation values which we'll use
      const assetsWithCalculations: Asset[] = apiEligibleAssets.map((eligibleAsset: EligibleAssetForDepreciation) => ({
        id: eligibleAsset.id,
        code: eligibleAsset.assetId,
        name: eligibleAsset.name,
        description: eligibleAsset.description ?? '',
        category: eligibleAsset.category,
        location: eligibleAsset.location,
        status: eligibleAsset.status as any,
        acquisitionCost: eligibleAsset.acquisitionCost,
        accumulatedDepreciation: eligibleAsset.accumulatedDepreciation,
        netBookValue: eligibleAsset.netBookValue,
        residualValue: eligibleAsset.residualValue,
        depreciationMethod: eligibleAsset.depreciationMethod as any,
        usefulLife: eligibleAsset.usefulLife ?? 5,
        acquisitionDate: eligibleAsset.acquisitionDate ?? '',
        // Store pre-calculated values in a way handleAssetsSelected can use
        // These will be used by the DepreciationCalculator or overridden
        _calculatedDepreciationValue: eligibleAsset.calculatedDepreciationValue,
        _calculatedClosingNBV: eligibleAsset.calculatedClosingNBV,
        _calculatedDepreciationPercent: eligibleAsset.calculatedDepreciationPercent,
        _formula: eligibleAsset.formula,
        _formulaBreakdown: eligibleAsset.formulaBreakdown,
      } as Asset & {
        _calculatedDepreciationValue: number;
        _calculatedClosingNBV: number;
        _calculatedDepreciationPercent?: number;
        _formula?: string;
        _formulaBreakdown?: string;
      }));
      
      // Create depreciation items directly with pre-calculated values from API
      handleAssetsSelectedWithCalculations(assetsWithCalculations, apiEligibleAssets);
    } catch (error) {
      console.error('Error fetching eligible assets:', error);
      toast.error('Failed to fetch eligible assets', {
        description: 'Please try again or use the asset selection dialog.',
      });
      // Fall back to mock assets on error
      handleAssetsSelected(mockAssets);
    } finally {
      setIsLoadingEligibleAssets(false);
    }
  };

  const isServerGeneratedId = (id: string) => {
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidRegex.test(id);
  };

  const handleViewLastBatchSummary = async () => {
    if (!completedBatches || completedBatches.length === 0) {
      toast.info('No batch history available', {
        description: 'There are no completed depreciation batches to view.',
      });
      return;
    }

    try {
      // Get the most recent batch (already sorted by date descending)
      const lastBatch = completedBatches[0];
      
      // Navigate to the batch view URL
      navigate(`/transactions/depreciation?batch=${lastBatch.id}`, {
        state: { viewBatch: true }
      });
      
      // If it's a server-generated ID, fetch the full batch details
      if (isServerGeneratedId(lastBatch.id)) {
        try {
          const fullBatch = await dataService.getDepreciationBatch(lastBatch.id);
          setCurrentBatch({
            ...fullBatch,
            dateOfRun: fullBatch.dateOfRun?.split('T')[0] ?? fullBatch.dateOfRun,
          });
          toast.success('Batch loaded', {
            description: `Viewing batch: ${fullBatch.batchReference ?? 'Untitled'}`,
          });
        } catch (error) {
          console.error('Failed to fetch batch details:', error);
          // Fallback to using the batch from the list
          setCurrentBatch({
            ...lastBatch,
                  dateOfRun: lastBatch.dateOfRun?.split('T')[0] ?? lastBatch.dateOfRun,
                });
                toast.success('Batch loaded', {
                  description: `Viewing batch: ${lastBatch.batchReference ?? 'Untitled'}`,
                });
              }
            } else {
              // Use the batch directly from the list
              setCurrentBatch({
                ...lastBatch,
                dateOfRun: lastBatch.dateOfRun?.split('T')[0] ?? lastBatch.dateOfRun,
              });
              toast.success('Batch loaded', {
                description: `Viewing batch: ${lastBatch.batchReference ?? 'Untitled'}`,
              });
            }
    } catch (error) {
      console.error('Error loading last batch:', error);
      toast.error('Failed to load batch', {
        description: 'An error occurred while loading the batch.',
      });
    }
  };

  const handleAddMoreAssets = () => {
    setShowAssetSelection(true);
  };

  const handleRemoveSelectedItems = () => {
    if (!currentBatch || selectedItemIds.size === 0) return;
    setShowRemoveConfirm(true);
  };

  const confirmRemoveSelectedItems = () => {
    if (!currentBatch || selectedItemIds.size === 0) return;
    
    const updatedItems = currentBatch.items.filter(item => !selectedItemIds.has(item.id));
    
    const totalDepreciation = updatedItems.reduce((sum, item) => sum + item.depreciationValue, 0);
    const totalOpeningNBV = updatedItems.reduce((sum, item) => sum + item.openingNBV, 0);
    const totalClosingNBV = updatedItems.reduce((sum, item) => sum + item.closingNBV, 0);
    
    setCurrentBatch({
      ...currentBatch,
      items: updatedItems,
      totalDepreciation,
      totalOpeningNBV,
      totalClosingNBV,
    });
    
    setSelectedItemIds(new Set());
  };

  const handleAssetsSelected = (selectedAssets: Asset[]) => {
    // Create depreciation items from selected assets
    const newItems: DepreciationItem[] = selectedAssets.map((asset, index) => {
      // Use the DepreciationCalculator to calculate depreciation based on asset's method
      const calculation = DepreciationCalculator.calculateDepreciation(asset, new Date(), 12);
      
      const usefulLifeYears = asset.usefulLife ?? 5;
      const depreciationMethod = asset.depreciationMethod ?? 'Straight Line';

      return {
        id: `dep-item-${Date.now()}-${index}`,
        assetId: asset.id,
        assetCode: asset.code,
        assetDescription: asset.name,
        category: asset.category,
        location: asset.location,
        openingNBV: asset.netBookValue,
        acquisitionCost: asset.acquisitionCost,
        accumulatedDepreciation: asset.accumulatedDepreciation,
        method: depreciationMethod as any,
        usefulLife: usefulLifeYears,
        lifeRemaining: usefulLifeYears - (asset.accumulatedDepreciation / asset.acquisitionCost) * usefulLifeYears,
        residualValue: asset.residualValue ?? 0,
        aiDepreciationPercent: calculation.depreciationPercent ?? 0,
        aiDepreciationValue: calculation.depreciationValue,
        aiClosingNBV: calculation.closingNBV,
        confidence: 'High' as const,
        depreciationPercent: calculation.depreciationPercent ?? 0,
        depreciationValue: calculation.depreciationValue,
        closingNBV: calculation.closingNBV,
        isManualOverride: false,
        validationStatus: 'valid',
        calculationInputs: calculation.calculationInputs ?? {
          method: depreciationMethod,
          residualValue: asset.residualValue ?? 0,
          formula: calculation.formula,
          formulaBreakdown: calculation.formulaBreakdown ?? '',
        },
      };
    });

    // If we have an existing batch, add to it; otherwise create new batch
    if (currentBatch) {
      // Filter out duplicates based on assetId
      const existingAssetIds = new Set(currentBatch.items.map(item => item.assetId));
      const uniqueNewItems = newItems.filter(item => !existingAssetIds.has(item.assetId));
      
      const allItems = [...currentBatch.items, ...uniqueNewItems];
      const totalDepreciation = allItems.reduce((sum, item) => sum + item.depreciationValue, 0);
      const totalOpeningNBV = allItems.reduce((sum, item) => sum + item.openingNBV, 0);
      const totalClosingNBV = allItems.reduce((sum, item) => sum + item.closingNBV, 0);
      
      setCurrentBatch({
        ...currentBatch,
        items: allItems,
        totalDepreciation,
        totalOpeningNBV,
        totalClosingNBV,
      });
    } else {
      // Create new batch
      const totalDepreciation = newItems.reduce((sum, item) => sum + item.depreciationValue, 0);
      const totalOpeningNBV = newItems.reduce((sum, item) => sum + item.openingNBV, 0);
      const totalClosingNBV = newItems.reduce((sum, item) => sum + item.closingNBV, 0);

      const batch: DepreciationBatch = {
        id: `batch-${Date.now()}`,
        batchReference: '',
        dateOfRun: new Date().toISOString().split('T')[0],
        projectId: '',
        projectName: '',
        status: 'In Progress',
        notes: '',
        items: newItems,
        createdBy: 'Current User',
        createdDate: new Date().toISOString(),
        modifiedBy: 'Current User',
        modifiedDate: new Date().toISOString(),
        preparedBy: 'Current User',
        preparedDate: new Date().toISOString(),
        currentStep: 'Prepare',
        totalDepreciation,
        totalOpeningNBV,
        totalClosingNBV,
        currency: 'GBP',
      };

      setCurrentBatch(batch);
    }
    
    setShowAssetSelection(false);
  };

  // Handle assets selected with pre-calculated depreciation values from API
  const handleAssetsSelectedWithCalculations = (
    selectedAssets: Asset[],
    eligibleAssetsData: EligibleAssetForDepreciation[]
  ) => {
    // Create a map for quick lookup of pre-calculated values
    const calculationsMap = new Map(
      eligibleAssetsData.map((ea) => [ea.id, ea])
    );

    // Create depreciation items using pre-calculated values from API
    const newItems: DepreciationItem[] = selectedAssets.map((asset, index) => {
      const apiCalc = calculationsMap.get(asset.id);
      const usefulLifeYears = asset.usefulLife ?? 5;
      const depreciationMethod = asset.depreciationMethod ?? 'Straight Line';

      // Use pre-calculated values from API if available, otherwise fall back to calculator
      const depreciationValue = apiCalc?.calculatedDepreciationValue ?? 0;
      const closingNBV = apiCalc?.calculatedClosingNBV ?? asset.netBookValue;
      const depreciationPercent = apiCalc?.calculatedDepreciationPercent ?? 0;
      
      // Use the DepreciationCalculator to get the full calculationInputs for "Why this value?" display
      // This ensures the calculation breakdown matches the actual calculation logic
      const calculation = DepreciationCalculator.calculateDepreciation(asset, new Date(), 12);

      return {
        id: `dep-item-${Date.now()}-${index}`,
        assetId: asset.id,
        assetCode: asset.code,
        assetDescription: asset.name,
        category: asset.category,
        location: asset.location,
        openingNBV: asset.netBookValue,
        acquisitionCost: asset.acquisitionCost,
        accumulatedDepreciation: asset.accumulatedDepreciation,
        method: depreciationMethod as any,
        usefulLife: usefulLifeYears,
        lifeRemaining: usefulLifeYears - (asset.accumulatedDepreciation / asset.acquisitionCost) * usefulLifeYears,
        residualValue: asset.residualValue ?? 0,
        aiDepreciationPercent: depreciationPercent,
        aiDepreciationValue: depreciationValue,
        aiClosingNBV: closingNBV,
        confidence: 'High' as const,
        depreciationPercent: depreciationPercent,
        depreciationValue: depreciationValue,
        closingNBV: closingNBV,
        isManualOverride: false,
        validationStatus: 'valid',
        calculationInputs: calculation.calculationInputs ?? {
          method: depreciationMethod,
          residualValue: asset.residualValue ?? 0,
          formula: apiCalc?.formula ?? '',
          formulaBreakdown: apiCalc?.formulaBreakdown ?? '',
        },
      };
    });

    // Create new batch with pre-calculated items
    const totalDepreciation = newItems.reduce((sum, item) => sum + item.depreciationValue, 0);
    const totalOpeningNBV = newItems.reduce((sum, item) => sum + item.openingNBV, 0);
    const totalClosingNBV = newItems.reduce((sum, item) => sum + item.closingNBV, 0);

    const batch: DepreciationBatch = {
      id: `batch-${Date.now()}`,
      batchReference: '',
      dateOfRun: new Date().toISOString().split('T')[0],
      projectId: '',
      projectName: '',
      status: 'In Progress',
      notes: '',
      items: newItems,
      createdBy: 'Current User',
      createdDate: new Date().toISOString(),
      modifiedBy: 'Current User',
      modifiedDate: new Date().toISOString(),
      preparedBy: 'Current User',
      preparedDate: new Date().toISOString(),
      currentStep: 'Prepare',
      totalDepreciation,
      totalOpeningNBV,
      totalClosingNBV,
      currency: currentPeriodSummary?.currency ?? 'GBP',
    };

    setCurrentBatch(batch);
    setShowAssetSelection(false);
  };

  const handleUpdateItem = (itemId: string, updates: Partial<DepreciationItem>) => {
    if (!currentBatch) return;

    const updatedItems = currentBatch.items.map((item) =>
      item.id === itemId ? { ...item, ...updates } : item
    );

    const totalDepreciation = updatedItems.reduce((sum, item) => sum + item.depreciationValue, 0);
    const totalClosingNBV = updatedItems.reduce((sum, item) => sum + item.closingNBV, 0);

    setCurrentBatch({
      ...currentBatch,
      items: updatedItems,
      totalDepreciation,
      totalClosingNBV,
    });
  };

  const handleSave = async () => {
    if (!currentBatch) return;
    
    try {
      const now = new Date().toISOString();
      const batchToSave = {
        ...currentBatch,
        modifiedBy: 'Current User',
        modifiedDate: now,
        status: currentBatch.status ?? 'In Progress',
      };
      
      if (currentBatch.id && isServerGeneratedId(currentBatch.id)) {
        const updatedBatch = await dataService.updateDepreciationBatch(currentBatch.id, batchToSave);
        setCurrentBatch({
          ...updatedBatch,
          dateOfRun: updatedBatch.dateOfRun?.split('T')[0] ?? updatedBatch.dateOfRun,
        });
      } else {
        const savedBatch = await dataService.createDepreciationBatch(batchToSave);
        setCurrentBatch({
          ...savedBatch,
          dateOfRun: savedBatch.dateOfRun?.split('T')[0] ?? savedBatch.dateOfRun,
        });
      }
      
      setShowModifiedHighlight(true);
      setTimeout(() => setShowModifiedHighlight(false), 2000);
      
      toast.success('Batch saved', { description: 'Depreciation batch saved successfully' });
    } catch (error) {
      console.error('Failed to save depreciation batch:', error);
      toast.error('Failed to save batch', { description: 'Please ensure you are in API mode and try again.' });
    }
  };

  const handleProcess = () => {
    if (!currentBatch) return;
    
    // Validate mandatory fields
    if (!currentBatch.batchReference || !currentBatch.batchReference.trim()) {
      toast.error('Validation Error', {
        description: 'Reference is required before processing',
      });
      return;
    }
    
    if (!currentBatch.dateOfRun) {
      toast.error('Validation Error', {
        description: 'Date is required before processing',
      });
      return;
    }
    
    setShowProcessConfirm(true);
  };

  const confirmProcess = async () => {
    if (!currentBatch) return;
    
    const reference = currentBatch.batchReference;
    
    try {
      let batchId = currentBatch.id;
      
      // Ensure batch is saved first to get a server-generated ID
      if (!isServerGeneratedId(currentBatch.id)) {
        const savedBatch = await dataService.createDepreciationBatch(currentBatch);
        batchId = savedBatch.id;
      } else {
        // Update the batch to ensure latest changes are saved
        await dataService.updateDepreciationBatch(currentBatch.id, currentBatch);
      }
      
      // Call the backend PostBatch endpoint which creates transactions and posts to Financials
      const result = await dataService.postDepreciationBatch(batchId, 'Current User');
      
      // Check for posting failures
      const postingResult = result?.postingResult;
      const hasCreationFailures = postingResult?.transactionCreationFailures?.length > 0;
      const hasPostingFailures = postingResult?.financialsPostingFailures?.length > 0;
      
      if (hasCreationFailures || hasPostingFailures) {
        // Build failure message
        const failureMessages: string[] = [];
        const errorDetails: string[] = [];
        
        if (hasCreationFailures) {
          const count = postingResult.transactionCreationFailures.length;
          failureMessages.push(`${count} transaction(s) failed to create`);
          postingResult.transactionCreationFailures.forEach((failure: { assetCode: string; errorMessage: string }) => {
            console.error(`Transaction creation failed for asset ${failure.assetCode}: ${failure.errorMessage}`);
            errorDetails.push(`Asset '${failure.assetCode}': ${formatApiErrorMessage(failure.errorMessage)}`);
          });
        }
        
        if (hasPostingFailures) {
          const count = postingResult.financialsPostingFailures.length;
          failureMessages.push(`${count} transaction(s) failed to post to Financials`);
          postingResult.financialsPostingFailures.forEach((failure: { assetCode: string; errorMessage: string }) => {
            console.error(`Financials posting failed for asset ${failure.assetCode}: ${failure.errorMessage}`);
            errorDetails.push(`Asset '${failure.assetCode}': ${formatApiErrorMessage(failure.errorMessage)}`);
          });
        }
        
        // Build description with error details (show first 3 errors, then indicate if more)
        const maxErrorsToShow = 3;
        const displayedErrors = errorDetails.slice(0, maxErrorsToShow);
        const remainingCount = errorDetails.length - maxErrorsToShow;
        let description = `Reference: ${reference}. ${failureMessages.join('. ')}.\n${displayedErrors.join('\n')}`;
        if (remainingCount > 0) {
          description += `\n...and ${remainingCount} more error(s)`;
        }
        
        // Show warning toast with failure details
        toast.warning('Depreciation Completed with Warnings', {
          description,
          duration: 10000,
        });
      }else {
        // Show success toast
        toast.success('Depreciation Created Successfully', {
          description: `Reference: ${reference}`,
        });
      }
      
      // Clear batch and navigate back to processing screen
      setCurrentBatch(null);
      setSelectedItemIds(new Set());
      setShowProcessConfirm(false);
      
      // Navigate back to processing screen
      navigate('/transactions/depreciation');
    } catch (error) {
      console.error('Failed to process depreciation batch:', error);
      toast.error('Failed to process depreciation batch', {
        description: 'Please ensure you are in API mode and try again.',
      });
      setShowProcessConfirm(false);
    }
  };

  const handleCancelBatch = () => {
    setShowCancelConfirm(true);
  };

  const confirmCancelBatch = () => {
    setCurrentBatch(null);
  };

  const handleExportExcel = () => {
    if (!currentBatch || !currentBatch.items || currentBatch.items.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      const columns: ExportColumn<DepreciationItem>[] = [
        { label: 'Asset Code', key: 'assetCode' as keyof DepreciationItem },
        { label: 'Asset Description', key: 'assetDescription' as keyof DepreciationItem },
        { label: 'Category', key: 'category' as keyof DepreciationItem },
        { label: 'Location', key: 'location' as keyof DepreciationItem },
        { label: 'Opening NBV', key: 'openingNBV' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
        { label: 'Acquisition Cost', key: 'acquisitionCost' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
        { label: 'Accumulated Depreciation', key: 'accumulatedDepreciation' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
        { label: 'Method', key: 'method' as keyof DepreciationItem },
        { label: 'Useful Life (Years)', key: 'usefulLife' as keyof DepreciationItem },
        { label: 'Life Remaining (Years)', key: 'lifeRemaining' as keyof DepreciationItem, format: (val: number) => val.toFixed(2) },
        { label: 'Residual Value', key: 'residualValue' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
        { label: 'Depreciation %', key: 'depreciationPercent' as keyof DepreciationItem, format: (val: number) => val ? `${val.toFixed(2)}%` : '' },
        { label: 'Depreciation Value', key: 'depreciationValue' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
        { label: 'Closing NBV', key: 'closingNBV' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
        { label: 'Manual Override', key: 'isManualOverride' as keyof DepreciationItem, format: (val: boolean) => val ? 'Yes' : 'No' },
        { label: 'Override Reason', key: 'overrideReason' as keyof DepreciationItem },
        { label: 'Notes', key: 'notes' as keyof DepreciationItem },
      ];

      exportToExcel({
        data: currentBatch.items,
        columns,
        filename: generateFilename(`depreciation-batch-${currentBatch.batchReference ?? 'draft'}`, 'xls'),
        sheetName: 'Depreciation Details',
      });

      toast.success('Export successful', {
        description: 'Depreciation batch exported to Excel',
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Export failed', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    }
  };

  // Use API data for period summary if available, otherwise fall back to mock calculations
  const estimatedPeriodDepreciation = currentPeriodSummary?.totalDepreciation ?? mockAssets.reduce((sum: number, asset: Asset) => {
    const usefulLifeYears = 5;
    const monthlyRate = 1 / (usefulLifeYears * 12);
    const depreciation = (asset.netBookValue - (asset.residualValue ?? 0)) * monthlyRate;
    return sum + depreciation;
  }, 0);

  // Use API data for current total NBV if available
  const currentTotalNBV = currentPeriodSummary?.totalCurrentNBV ?? mockAssets.reduce((sum: number, asset: Asset) => sum + asset.netBookValue, 0);
  
  // Use API data for expected NBV if available
  const expectedTotalNBV = currentPeriodSummary?.totalExpectedNBV ?? (currentTotalNBV - estimatedPeriodDepreciation);
  
  // Use API data for assets in scope count
  const assetsInScopeCount = currentPeriodSummary?.assetsInScope ?? mockAssets.length;
  
  // Check if period is up to date (no assets eligible)
  const isPeriodUpToDate = currentPeriodSummary?.isUpToDate ?? false;

  // Landing Dashboard View
  if (!currentBatch) {
    return (
      <div className="flex h-full flex-col">
        {/* Page Header */}
        <div className="border-b bg-card p-6" data-tour="depreciation-header">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <TrendingDown className="h-6 w-6 text-primary" />
                Depreciations
              </h1>
              <p className="text-sm text-muted-foreground mt-1">View, create, or edit transactions that reduce asset net book value over time.</p>
            </div>
                    <div className="flex items-center gap-2">
                      <EvoFeatureFeedbackCollector feature="Depreciation Transactions" />
                      <Button variant="outline" onClick={handleViewLastBatchSummary} data-tour="view-summary-button">
                        <Eye className="mr-2 h-4 w-4" />
                        View Last Batch Summary
                      </Button>
                      <Button onClick={handleStartRun} data-tour="start-batch-button">
                        <Play className="mr-2 h-4 w-4" />
                        Start New Depreciation Run
                      </Button>
                    </div>
                    </div>
                  </div>

                {/* Content */}
        <div className="flex-1 overflow-auto">
          <div className="space-y-6 p-6">
        {/* Current Period Status - Wrapped with gradient border */}
        <div data-tour="current-period">
          <div 
            className="rounded-xl p-[5px]"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
            }}
          >
            <Card className="border-0 rounded-[10px] relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-500/5 opacity-40" />
              <CardContent className="relative p-6 space-y-6">
                <div className="flex flex-col 2xl:flex-row 2xl:items-center 2xl:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h2 className="text-2xl font-semibold">Current Period: {period.period}</h2>
                      <Sparkles className="h-5 w-5 text-purple-500" />
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">Suggested depreciation run for current period</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-4 right-4 h-8 w-8"
                    onClick={() => toast.info('Close functionality coming soon')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                {isPeriodUpToDate ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="text-center">
                      <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-2" />
                      <div className="text-lg font-semibold text-green-700 dark:text-green-400">
                        {currentPeriodSummary?.upToDateMessage ?? 'All assets are up to date'}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        No more assets are marked for depreciation this period
                      </p>
                    </div>
                  </div>
                ) : isLoadingPeriodSummary ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="text-center">
                      <Clock className="h-8 w-8 text-muted-foreground mx-auto mb-2 animate-pulse" />
                      <p className="text-sm text-muted-foreground">Loading period summary...</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4 2xl:gap-6">
                    <div className="space-y-1">
                      <div className="text-xs 2xl:text-sm text-muted-foreground">Assets in Scope</div>
                      <div className="text-lg 2xl:text-2xl font-bold">{assetsInScopeCount.toLocaleString()}</div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs 2xl:text-sm text-muted-foreground">Current NBV</div>
                      <div className="text-lg 2xl:text-2xl font-bold">£{currentTotalNBV.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs 2xl:text-sm text-muted-foreground">Expected NBV</div>
                      <div className="text-lg 2xl:text-2xl font-bold">
                        £{expectedTotalNBV.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                        <span className="text-xs 2xl:text-sm text-red-600 ml-1 2xl:ml-2 block 2xl:inline">
                          - £{estimatedPeriodDepreciation.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {period.status === 'Not Started' && !isPeriodUpToDate && (
                <div className="pt-4 border-t">
                  <div className="flex items-center justify-end">
                    <Button 
                      size="lg" 
                      onClick={handleStartRunWithAllAssets}
                      disabled={isLoadingEligibleAssets || isLoadingPeriodSummary}
                    >
                      {isLoadingEligibleAssets ? (
                        <>
                          <Clock className="mr-2 h-5 w-5 animate-spin" />
                          Loading Assets...
                        </>
                      ) : (
                        <>
                          <Play className="mr-2 h-5 w-5" />
                          Start Depreciation Run
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
              
              {period.status === 'Completed' && period.completedBy && (
                <div className="pt-4 border-t">
                  <div className="text-sm text-muted-foreground">Completed</div>
                  <div className="text-sm font-medium">
                    {period.completedBy} on {period.completedDate && new Date(period.completedDate).toLocaleString('en-GB')}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        </div>

        {/* Transaction History */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold">Transaction History</h2>
              <p className="text-sm text-muted-foreground mt-1">View and manage all depreciation transactions</p>
            </div>
            <div className="relative w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
                className="pl-9 bg-white dark:bg-slate-900"
                data-tour="search-transactions"
              />
            </div>
          </div>
          
          {(() => {
            if (isLoadingBatches) {
              return (
                <div className="flex h-64 items-center justify-center">
                  <div className="text-center">
                    <p className="text-lg font-medium text-slate-700 dark:text-slate-300">Loading transactions...</p>
                  </div>
                </div>
              );
            }
            
            // Filter batches
            let filteredBatches = completedBatches.filter((batch) => {
              if (!searchQuery) return true;
              const query = searchQuery.toLowerCase();
              return (
                batch.batchReference.toLowerCase().includes(query) ||
                batch.status.toLowerCase().includes(query) ||
                (batch.postedBy && batch.postedBy.toLowerCase().includes(query))
              );
            });
            
            // Sort batches
            filteredBatches = [...filteredBatches].sort((a, b) => {
              if (!sortConfig.key || !sortConfig.direction) return 0;
              const aValue = getBatchSortValue(a, sortConfig.key);
              const bValue = getBatchSortValue(b, sortConfig.key);
              return compareSortValues(aValue, bValue, sortConfig.direction);
            });
            
            // Pagination calculations
            const totalPages = Math.ceil(filteredBatches.length / pageSize);
            const startIndex = (currentPage - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            const paginatedBatches = filteredBatches.slice(startIndex, endIndex);
            
            if (filteredBatches.length === 0) {
              return (
                <div className="flex h-64 items-center justify-center">
                  <div className="text-center space-y-3">
                    <div className="flex justify-center">
                      <div className="rounded-full bg-slate-100 dark:bg-slate-800 p-4">
                        <FileText className="h-8 w-8 text-slate-400 dark:text-slate-500" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">No transactions found</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {searchQuery ? "Try adjusting your search" : "No depreciation transactions available yet"}
                      </p>
                    </div>
                  </div>
                </div>
              );
            }
            
            return (
              <div className="border rounded-lg overflow-hidden" data-tour="transaction-history">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
                    <TableRow className="border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                      <TableHead className="h-12 font-semibold">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8 data-[state=open]:bg-accent hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                          onClick={() => handleSort('batchReference')}
                        >
                          Reference
                          <SortIcon columnKey="batchReference" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                        </Button>
                      </TableHead>
                      <TableHead className="h-12 font-semibold">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8 data-[state=open]:bg-accent hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                          onClick={() => handleSort('dateOfRun')}
                        >
                          Date
                          <SortIcon columnKey="dateOfRun" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                        </Button>
                      </TableHead>
                      <TableHead className="h-12 font-semibold">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8 data-[state=open]:bg-accent hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                          onClick={() => handleSort('period')}
                        >
                          Period
                          <SortIcon columnKey="period" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                        </Button>
                      </TableHead>
                      <TableHead className="h-12 font-semibold">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8 data-[state=open]:bg-accent hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                          onClick={() => handleSort('year')}
                        >
                          Year
                          <SortIcon columnKey="year" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                        </Button>
                      </TableHead>
                      <TableHead className="h-12 font-semibold">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8 data-[state=open]:bg-accent hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                          onClick={() => handleSort('assetCount')}
                        >
                          Assets
                          <SortIcon columnKey="assetCount" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                        </Button>
                      </TableHead>
                      <TableHead className="h-12 font-semibold text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-mr-3 h-8 data-[state=open]:bg-accent ml-auto flex hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                          onClick={() => handleSort('totalDepreciation')}
                        >
                          Total Depreciation
                          <SortIcon columnKey="totalDepreciation" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                        </Button>
                      </TableHead>
                      <TableHead className="h-12 font-semibold">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8 data-[state=open]:bg-accent hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                          onClick={() => handleSort('postedBy')}
                        >
                          Posted By
                          <SortIcon columnKey="postedBy" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                        </Button>
                      </TableHead>
                      <TableHead className="h-12 font-semibold">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8 data-[state=open]:bg-accent hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                          onClick={() => handleSort('postedDate')}
                        >
                          Posted Date
                          <SortIcon columnKey="postedDate" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                        </Button>
                      </TableHead>
                      <TableHead className="h-12 font-semibold">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8 data-[state=open]:bg-accent hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                          onClick={() => handleSort('status')}
                        >
                          Status
                          <SortIcon columnKey="status" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                        </Button>
                      </TableHead>
                      <TableHead className="h-12 font-semibold text-right"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedBatches.map((batch, index) => (
                      <TableRow 
                        key={batch.id}
                        className={`
                          ${index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-50/50 dark:bg-slate-900/50'}
                          hover:bg-slate-100 dark:hover:bg-slate-800 
                          border-b border-slate-100 dark:border-slate-800
                          transition-colors
                        `}
                        data-tour={index === 0 ? "batch-calculations" : undefined}
                      >
                        <TableCell className="font-medium text-slate-900 dark:text-slate-100">
                          {batch.batchReference}
                        </TableCell>
                        <TableCell className="text-slate-700 dark:text-slate-300">
                          {new Date(batch.dateOfRun).toLocaleDateString('en-GB')}
                        </TableCell>
                        <TableCell className="text-slate-700 dark:text-slate-300">
                          {batch.period ?? '-'}
                        </TableCell>
                        <TableCell className="text-slate-700 dark:text-slate-300">
                          {batch.year ?? '-'}
                        </TableCell>
                        <TableCell className="text-slate-700 dark:text-slate-300">
                          {batch.items?.length ?? 0}
                        </TableCell>
                        <TableCell className="text-right text-slate-700 dark:text-slate-300 tabular-nums">
                          {formatCurrency(batch.totalDepreciation)}
                        </TableCell>
                        <TableCell className="text-slate-700 dark:text-slate-300">
                          {batch.postedBy ?? '-'}
                        </TableCell>
                        <TableCell className="text-slate-700 dark:text-slate-300">
                          {batch.postedDate 
                            ? new Date(batch.postedDate).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : '-'
                          }
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="secondary"
                            className={
                              batch.status === 'In Progress'
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            }
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            {batch.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              // Format the date to YYYY-MM-DD for the date input
                              const formattedBatch = {
                                ...batch,
                                dateOfRun: batch.dateOfRun.split('T')[0]
                              };
                              setCurrentBatch(formattedBatch);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {/* Pagination and Summary */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between gap-4 px-4 py-3 border-t bg-slate-50/50 dark:bg-slate-900/50">
                    <div className="text-sm text-muted-foreground flex-shrink-0">
                      Showing {startIndex + 1}-{Math.min(endIndex, filteredBatches.length)} of {filteredBatches.length} transactions
                      {filteredBatches.length !== completedBatches.length && ` (filtered from ${completedBatches.length} total)`}
                    </div>
                    
                    {/* Pagination Controls */}
                    <div className="flex items-center justify-center gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                      >
                        First
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                      
                      {/* Page Number Buttons */}
                      <div className="flex items-center gap-1">
                        {generatePageNumbers(currentPage, totalPages).map((page, pageIndex, pageNumbers) => {
                          if (page === '...') {
                            return (
                              <span key={`ellipsis-before-${pageNumbers[pageIndex + 1]}`} className="px-2 text-muted-foreground">
                                ...
                              </span>
                            );
                          }
                          
                          return (
                            <Button
                              key={`page-${page}`}
                              variant={currentPage === page ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setCurrentPage(page as number)}
                              className="min-w-[36px]"
                            >
                              {page}
                            </Button>
                          );
                        })}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        Last
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

          </div>
        </div>

        <AssetSelectionDialog
          open={showAssetSelection}
          onOpenChange={setShowAssetSelection}
          onConfirm={handleAssetsSelected}
          excludeAssetIds={[]}
        />

        <ComingSoonModal
          open={showComingSoon}
          onOpenChange={setShowComingSoon}
          title="Run Summary Coming Soon"
          description="The ability to view the last run summary will be available in an upcoming release."
        />

        <DemoModeWarningModal
          open={showDemoWarning}
          onOpenChange={setShowDemoWarning}
          onSwitchToLive={() => setMode('live')}
        />

        {/* Product Tour */}
        <ProductTour
          steps={tourSteps}
          isOpen={isTourOpen}
          onClose={closeTour}
          onComplete={completeTour}
        />
      </div>
    );
  }

  // Batch Processing View
  const readonly = currentBatch.status === 'Completed';

  return (
    <div className="relative flex flex-col h-full">
      <div className="flex-1 overflow-auto pb-32">
      {/* Page Header */}
      <div className="bg-card p-6 pb-0">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <TrendingDown className="h-6 w-6 text-primary" />
              Depreciations
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Last Modified{' '}
              <span 
                className={`font-semibold transition-colors duration-500 ${
                  showModifiedHighlight ? 'text-green-600 dark:text-green-400' : 'text-foreground'
                }`}
              >
                {currentBatch.modifiedDate ? new Date(currentBatch.modifiedDate).toLocaleString('en-GB', { 
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }).replace(/(\d+)/, (match) => {
                  const day = parseInt(match);
                  const suffix = day === 1 || day === 21 || day === 31 ? 'st' : 
                                 day === 2 || day === 22 ? 'nd' : 
                                 day === 3 || day === 23 ? 'rd' : 'th';
                  return `${day}${suffix}`;
                }) : 'Never'}
              </span>
            </p>
          </div>
          <Badge
            variant="secondary"
            className={`text-base px-3 py-1 ${
              currentBatch.status === 'In Progress'
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            }`}
          >
            {currentBatch.status === 'Completed' && <CheckCircle2 className="h-3 w-3 mr-1" />}
            {currentBatch.status === 'In Progress' && <Clock className="h-3 w-3 mr-1" />}
            {currentBatch.status}
          </Badge>
        </div>
        
        <div className="-mx-6 px-6 pt-6 pb-4 border-y border-border bg-[#F9FAFB] dark:bg-[#121212]">
          <div className="space-y-4">
            <div className="flex gap-4">
              {/* Left side: Reference and Date stacked */}
              <div className="flex flex-col gap-4 flex-shrink-0" style={{ width: '300px' }}>
                <div className="space-y-2">
                  <Label htmlFor="batch-ref" className="font-semibold">
                    Reference <span className="text-red-500">*</span>
                  </Label>
                  <Input 
                    id="batch-ref" 
                    value={currentBatch.batchReference} 
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCurrentBatch({ ...currentBatch, batchReference: e.target.value })}
                    readOnly={readonly} 
                    placeholder="Enter reference"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date-of-run" className="font-semibold">
                    Date <span className="text-red-500">*</span>
                  </Label>
                  <Input 
                    id="date-of-run" 
                    type="date" 
                    value={currentBatch.dateOfRun} 
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCurrentBatch({ ...currentBatch, dateOfRun: e.target.value })}
                    readOnly={readonly} 
                  />
                </div>
              </div>

              {/* Right side: Notes textarea spanning full height */}
              <div className="flex-1 flex flex-col">
                <Label htmlFor="notes" className="mb-2 font-semibold">Notes</Label>
                <Textarea
                  id="notes"
                  value={currentBatch.notes}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCurrentBatch({ ...currentBatch, notes: e.target.value })}
                  placeholder="Add notes about this depreciation run..."
                  className="flex-1 resize-none h-[104px] bg-white dark:bg-gray-950"
                  readOnly={readonly}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Grid Header */}
      <div className="bg-card px-6 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold">Depreciation Details</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {currentBatch.items.length} {currentBatch.items.length === 1 ? 'asset' : 'assets'} added to this batch
            </p>
          </div>
          {!readonly && (
            <div className="flex items-center gap-3">
              {/* AI Calculation Info Message */}
              <Button variant="default" className="h-[42px]" onClick={handleAddMoreAssets}>
                <Plus className="h-4 w-4 mr-2" />
                Add Assets
              </Button>
              {selectedItemIds.size > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Settings2 className="h-4 w-4 mr-2" />
                      Actions ({selectedItemIds.size})
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleRemoveSelectedItems} className="text-destructive">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove Selected
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Table Container - Full height with no padding */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        <BatchDetailGrid 
          items={currentBatch.items} 
          onUpdateItem={handleUpdateItem} 
          readonly={readonly}
          selectedItemIds={selectedItemIds}
          onSelectionChange={setSelectedItemIds}
        />
      </div>
      
      <AssetSelectionDialog
        open={showAssetSelection}
        onOpenChange={setShowAssetSelection}
        onConfirm={handleAssetsSelected}
        excludeAssetIds={currentBatch?.items.map(item => item.assetId) ?? []}
      />

      {/* Confirmation Modals */}
      <ConfirmationModal
        open={showCancelConfirm}
        onOpenChange={setShowCancelConfirm}
        onConfirm={confirmCancelBatch}
        title="Cancel Depreciation Batch?"
        description="Are you sure you want to cancel this batch? All unsaved changes will be lost and cannot be recovered."
        confirmText="Yes, Cancel Batch"
        cancelText="Keep Working"
        type="warning"
      />

      <ConfirmationModal
        open={showProcessConfirm}
        onOpenChange={setShowProcessConfirm}
        onConfirm={confirmProcess}
        title="Process Depreciation Batch"
        description={
          <div className="space-y-2">
            <p>This will post the depreciation transactions to the ledger.</p>
            <div className="mt-4 p-3 bg-muted rounded-md space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Assets:</span>
                <span className="font-medium">{currentBatch?.items.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Depreciation:</span>
                <span className="font-medium">£{currentBatch?.totalDepreciation.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-3">This action cannot be undone.</p>
          </div>
        }
        confirmText="Process Batch"
        cancelText="Review Again"
        type="success"
      />

      <ConfirmationModal
        open={showRemoveConfirm}
        onOpenChange={setShowRemoveConfirm}
        onConfirm={confirmRemoveSelectedItems}
        title="Remove Selected Assets"
        description={`Are you sure you want to remove ${selectedItemIds.size} selected asset(s) from this batch? This action cannot be undone.`}
        confirmText="Remove Assets"
        cancelText="Cancel"
        type="danger"
      />

      </div>

      {/* Sticky Footer - Pinned to bottom */}
      <div className="absolute bottom-0 left-0 right-0 bg-background border-t shadow-lg">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Action Buttons */}
            <div className="flex gap-2">
              {currentBatch.status === 'In Progress' && (
                <>
                  <Button variant="outline" onClick={handleCancelBatch}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button variant="outline" onClick={handleSave}>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                  <Button onClick={handleProcess}>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Save & Process
                  </Button>
                </>
              )}
              {currentBatch.status === 'Completed' && (
                <>
                  <Button variant="outline" onClick={() => setCurrentBatch(null)}>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Back to Dashboard
                  </Button>
                </>
              )}
            </div>

            {/* Center: Spacer */}
            <div className="flex-1" />

            {/* Right: Summary Totals */}
            <div className="flex gap-8">
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Total Opening NBV</div>
                <div className="text-lg font-bold">
                  £{currentBatch.totalOpeningNBV.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Total Depreciation</div>
                <div className="text-lg font-bold text-red-600">
                  £{currentBatch.totalDepreciation.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Total Closing NBV</div>
                <div className="text-lg font-bold">
                  £{currentBatch.totalClosingNBV.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Product Tour */}
      <ProductTour
        steps={tourSteps}
        isOpen={isTourOpen}
        onClose={closeTour}
        onComplete={completeTour}
      />
    </div>
  );
}

export default function DepreciationPage(): React.ReactElement {
  return (
    <FeatureSettingsGuard>
      <DepreciationPageContent />
    </FeatureSettingsGuard>
  );
}
